import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/primitives";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ImageUp, Loader2, Trash2, Image as ImageIcon } from "lucide-react";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export default function HotelLogoUploader() {
  const [mediaId, setMediaId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/branding");
      setMediaId(data.logo_media_id || null);
    } catch { /* non-critical */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file (PNG, JPG, WebP)"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Logo must be under 10 MB"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("entity_type", "hotel_logo");
      const { data: up } = await api.post("/media/v1/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.put("/branding/logo", { media_id: up.id });
      setMediaId(up.id);
      toast.success("Logo updated — it will now appear on bills, registration cards and reports");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete("/branding/logo");
      setMediaId(null);
      toast.success("Logo removed");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const logoSrc = mediaId ? `${BACKEND}/api/media/v1/${mediaId}` : null;

  return (
    <Card className="p-6 mb-4" data-testid="hotel-logo-card">
      <div className="text-[10px] uppercase tracking-[0.15em] text-hocp-textMuted mono mb-3 pb-2 border-b border-hocp-border">
        Hotel Logo
      </div>
      <div className="flex items-center gap-5">
        <div
          className="w-28 h-28 shrink-0 rounded-xl border border-dashed border-zinc-600 bg-zinc-900/40 flex items-center justify-center overflow-hidden"
          data-testid="hotel-logo-preview"
        >
          {loading ? (
            <Loader2 className="w-5 h-5 animate-spin text-hocp-textMuted" />
          ) : logoSrc ? (
            <img src={logoSrc} alt="Hotel logo" className="max-w-full max-h-full object-contain" />
          ) : (
            <ImageIcon className="w-8 h-8 text-hocp-textTertiary" />
          )}
        </div>
        <div className="flex-1">
          <p className="text-[13px] text-hocp-textMuted mb-3 leading-snug">
            Upload your hotel's logo (PNG, JPG or WebP, up to 10 MB). It appears on every generated
            bill/invoice, guest registration card and report.
          </p>
          <div className="flex items-center gap-2">
            <input ref={fileRef} type="file" accept="image/*" className="hidden"
                   onChange={onPick} data-testid="hotel-logo-input" />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="h-9 px-4 text-[13px]"
              data-testid="hotel-logo-upload-btn"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    : <ImageUp className="w-3.5 h-3.5 mr-1.5" />}
              {mediaId ? "Replace logo" : "Upload logo"}
            </Button>
            {mediaId && (
              <Button variant="outline" onClick={remove} disabled={busy}
                      className="h-9 px-3 text-[13px]" data-testid="hotel-logo-remove-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Remove
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
